// First-party analytics: every visit and contact action becomes one small document in
// Firestore (`analytics_events`), which the admin Metrics tab aggregates. No cookies and no
// personal data: the session id is random and lives in sessionStorage only.
// See docs/metrics.md for the event list, the security rules and the cost notes.
import { addDoc, collection, serverTimestamp, Timestamp } from "firebase/firestore";
import { db } from "../firebase/firebase";
import { splitPath } from "../i18n";

export const EVENTS_COLLECTION = "analytics_events";

export const EVENT_TYPES = [
  "page_view",
  "call",
  "viber",
  "whatsapp",
  "email",
  "lead",
  "view_content",
  "inquiry_open",
  "gallery_open",
  "map_open",
  "social",
  "lang_switch",
  "cta",
];

const RETENTION_DAYS = 400; // matched by a Firestore TTL policy on `expiresAt`

const clip = (value, max) => (value == null || value === "" ? undefined : String(value).trim().slice(0, max));

function storage() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function sessionInfo() {
  const store = storage();
  let id = store?.getItem("tehnocin.sid");
  let entry = false;
  if (!id) {
    id = Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    entry = true;
    store?.setItem("tehnocin.sid", id);
  }
  return { id, entry };
}

function device() {
  const w = window.innerWidth || 1024;
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

// Acquisition data is captured once, on the first event of the session.
function acquisition() {
  const params = new URLSearchParams(window.location.search);
  let referrer;
  try {
    const host = document.referrer ? new URL(document.referrer).hostname : "";
    if (host && host !== window.location.hostname) referrer = host.replace(/^www\./, "");
  } catch {
    /* malformed referrer */
  }
  // Meta ads add fbclid, Google ads gclid; count them as paid traffic when no UTM is set.
  const utmSource = params.get("utm_source") || (params.get("fbclid") ? "facebook" : params.get("gclid") ? "google" : null);
  const utmMedium = params.get("utm_medium") || (params.get("fbclid") || params.get("gclid") ? "paid" : null);
  return {
    referrer: clip(referrer, 120),
    utm_source: clip(utmSource, 80),
    utm_medium: clip(utmMedium, 80),
    utm_campaign: clip(params.get("utm_campaign"), 120),
  };
}

export function trackingEnabled() {
  if (typeof window === "undefined") return false;
  if (window.location.pathname.startsWith("/admin")) return false;
  const ua = navigator.userAgent || "";
  if (/TehnocinPrerender|bot|crawler|spider|Lighthouse/i.test(ua)) return false;
  if (navigator.webdriver && process.env.REACT_APP_TRACK_IN_TESTS !== "true") return false;
  return true;
}

/**
 * Records one event. Never throws and never blocks the UI; a failed write is ignored.
 * @param {string} type  one of EVENT_TYPES
 * @param {{source?: string, label?: string}} [details]  source = where on the page (e.g. "hero")
 */
export function track(type, { source, label } = {}) {
  if (!trackingEnabled() || !EVENT_TYPES.includes(type)) return;
  try {
    const { lang } = splitPath(window.location.pathname);
    const session = sessionInfo();
    const data = {
      type,
      path: clip(window.location.pathname, 200) || "/",
      lang,
      session: session.id,
      device: device(),
      timestamp: serverTimestamp(),
      expiresAt: Timestamp.fromMillis(Date.now() + RETENTION_DAYS * 86400000),
    };
    const extra = { source: clip(source, 40), label: clip(label, 160) };
    if (session.entry) Object.assign(extra, acquisition(), { entry: true });
    Object.entries(extra).forEach(([key, value]) => value !== undefined && (data[key] = value));
    addDoc(collection(db, EVENTS_COLLECTION), data).catch(() => {});
  } catch {
    /* analytics must never break the site */
  }
}
